import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../services/fpResourcesApi';
import type {
  WebinarLinkData,
  AdvisorAcceleratorLinkData,
  UpdateWebinarLinkPayload,
  UpdateAdvisorAcceleratorLinkPayload,
  UpdateCommunicationAssetPayload,
} from '../types/fp-resources.types';

export const fpResourceKeys = {
  communicationAsset: (fpId: string) => ['admin', 'fp-resources', 'communication-asset', fpId] as const,
  webinarLink: () => ['admin', 'fp-resources', 'webinar-link'] as const,
  advisorAcceleratorLink: () => ['admin', 'fp-resources', 'advisor-accelerator-link'] as const,
};

// --- Communication Asset (per-FP) ---

export function useCommunicationAsset(fpId: string) {
  return useQuery({
    queryKey: fpResourceKeys.communicationAsset(fpId),
    queryFn: () => api.getCommunicationAsset(fpId),
    enabled: !!fpId,
    refetchOnWindowFocus: false,
  });
}

export function useSaveCommunicationAsset(fpId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCommunicationAssetPayload) =>
      api.saveCommunicationAsset(fpId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(fpResourceKeys.communicationAsset(fpId), data);
    },
  });
}

// --- Webinar Link (singleton) ---

export function useWebinarLink() {
  return useQuery({
    queryKey: fpResourceKeys.webinarLink(),
    queryFn: api.getWebinarLink,
    refetchOnWindowFocus: false,
  });
}

export function useSaveWebinarLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateWebinarLinkPayload) => api.saveWebinarLink(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: fpResourceKeys.webinarLink() });
      const previous = queryClient.getQueryData<WebinarLinkData>(fpResourceKeys.webinarLink());
      queryClient.setQueryData<WebinarLinkData>(fpResourceKeys.webinarLink(), (old) => ({
        id: old?.id ?? 'optimistic',
        updatedAt: new Date().toISOString(),
        url: payload.url,
        buttonLabel: payload.buttonLabel ?? null,
        description: payload.description ?? null,
        enabled: payload.enabled,
      }));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(fpResourceKeys.webinarLink(), ctx.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(fpResourceKeys.webinarLink(), data);
    },
  });
}

export function useDeleteWebinarLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.deleteWebinarLink,
    onSuccess: () => {
      queryClient.setQueryData(fpResourceKeys.webinarLink(), null);
    },
  });
}

// --- Advisor Accelerator Link (singleton) ---

export function useAdvisorAcceleratorLink() {
  return useQuery({
    queryKey: fpResourceKeys.advisorAcceleratorLink(),
    queryFn: api.getAdvisorAcceleratorLink,
    refetchOnWindowFocus: false,
  });
}

export function useSaveAdvisorAcceleratorLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAdvisorAcceleratorLinkPayload) =>
      api.saveAdvisorAcceleratorLink(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: fpResourceKeys.advisorAcceleratorLink() });
      const previous = queryClient.getQueryData<AdvisorAcceleratorLinkData>(
        fpResourceKeys.advisorAcceleratorLink(),
      );
      queryClient.setQueryData<AdvisorAcceleratorLinkData>(
        fpResourceKeys.advisorAcceleratorLink(),
        (old) => ({
          id: old?.id ?? 'optimistic',
          updatedAt: new Date().toISOString(),
          url: payload.url,
          buttonLabel: payload.buttonLabel ?? null,
          description: payload.description ?? null,
          enabled: payload.enabled,
        }),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(fpResourceKeys.advisorAcceleratorLink(), ctx.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(fpResourceKeys.advisorAcceleratorLink(), data);
    },
  });
}

export function useDeleteAdvisorAcceleratorLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.deleteAdvisorAcceleratorLink,
    onSuccess: () => {
      queryClient.setQueryData(fpResourceKeys.advisorAcceleratorLink(), null);
    },
  });
}
